<script lang="ts">
    import type {ITreeListNode} from 'src/components/#common/tree-list/TreeListNode'
    import {TreeListNode} from 'src/components/#common/tree-list/TreeListNode'
    import Categories from 'src/components/cabinet/app-configs/components/Categories.svelte'
    import {deepClone} from 'src/components/#common/tree-list/deepClone'
    import IconifyIcon from '@iconify/svelte'
    import copyIcon from '@iconify/icons-fa-solid/copy'
    import addChildIcon from '@iconify/icons-fa-solid/folder-plus'
    import downIcon from '@iconify/icons-fa-solid/chevron-down'
    import {createEventDispatcher} from 'svelte'
    import type {CategoryState} from 'src/components/cabinet/app-configs/components/contracts'
    import {dragObject} from 'src/components/cabinet/app-configs/components/dragObject'
    import {doShow as showAlert} from 'src/components/#common/alert/store'
    import {isDragging} from 'src/components/cabinet/app-configs/components/isDragging'
    import {categoriesToObject, editableToObject} from 'src/components/cabinet/app-configs/components/helpers'

    const dispatch = createEventDispatcher()

    export let level: number = 0
    export let category
    export let index
    export let firstDraggable
    export let maxLevels: number = 5

    export let searchText = ''

    let status = false
    let isHovering = false
    let hoveringIndex: number
    let isSearchFound = false

    $: searchText, isSearchFound = false, findByKey(categoriesToObject(category))

    function allowToDrop(_category: ITreeListNode<CategoryState>) {
        return _category
            // ограничение максимальной глубины
            && _category.maxDepth + level < maxLevels
            // защита от бесконечной вложенности
            && !_category.isParentOf(category.parent)
    }

    const drop = (event, index) => {
        if ($dragObject == null) {
            console.warn('dragObject is null')
            return
        }

        if (!allowToDrop($dragObject?.object)) {
            showAlert.set({
                status: 'message',
                text: 'WARNING! The nesting level of the selected category is too large.'
            })
            return
        }

        event.dataTransfer.dropEffect = 'move'

        // вставляем категорию по индексу с автоматическим удалением из предыдущего родителя
        const { object } = $dragObject
        category.parent.insert(index, object)
        hoveringIndex = null

        if (!firstDraggable) {
            firstDraggable = true
            showAlert.set({
                status: 'message',
                text: 'WARNING! You have changed the category nesting. The config may not work correctly.'
            })
        }
    }

    const dragstart = (event, index) => {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.dropEffect = 'move'

        $dragObject = {
            object: category.parent.get(index),
            dropped: false,
        }
    }

    const dragend = () => {
        $dragObject = null
    }

    function addCategory(category) {
        category.insert(0, new TreeListNode({
            data: {
                name: 'Category ' + (category.length + 1),
                description: '',
                type: 'object',
                value: {
                    id: category.length,
                }
            },
        }))
    }

    function findByKey(jsonObj) {
        if (jsonObj instanceof Array) {
            // если массив, рекурсивно вызываем для каждого элемента
            for (let i = 0; i < jsonObj.length; i++) {
                if (findByKey(jsonObj[i])) {
                    isSearchFound = true
                }
            }
        } else if (jsonObj instanceof Object) {
            // превращаем искаемый текст в регулярное выражение
            const regex = new RegExp(searchText, 'i')

            // проверяем, есть ли в ключах объекта искаемый текст
            for (let prop in jsonObj) {
                if (jsonObj.hasOwnProperty(prop)) {
                    if (regex.test(prop)) {
                        isSearchFound = true
                        break
                    }

                    // рекурсивно ищем внутренние объекты
                    if (jsonObj[prop] instanceof Object) {
                        if (findByKey(jsonObj[prop])) {
                            isSearchFound = true
                            break
                        }
                    }
                }
            }
        }
    }

    function editableToObj(object) {
        // arrays and objects

        if (object.type === 'array') {
            return {
                value      : object.value.map(editableToObject),
                description: object.description,
            }
        }

        if (object.type === 'object') {
            return object.value.reduce((result, [key, value]) => {
                result[key] = editableToObject(value)
                return result
            }, {})
        }

        return {
            value      : object.value,
            description: object.description,
        }
    }

    $: if ($dragObject == null) {
        hoveringIndex = null
    }
</script>
{#if !searchText || isSearchFound}
<div class="list-item" class:is-hover={isHovering}>
    <div class="list-item__name"
        class:is-active={hoveringIndex === index}
        class:is-selected={category.data.selected}
        on:dragenter={() => {
             hoveringIndex = index
        }}
        on:dragleave={() => {
            if (hoveringIndex === index) {
                hoveringIndex = null
            }
        }}
        on:dragend={dragend}
        ondragover="return false"
        on:drop|preventDefault={event => { drop(event, index) }}
        draggable={true}
        on:dragstart={event => { dragstart(event, index) }}
        on:click={() => {
            dispatch('select', category.parent.get(index))
        }}
    >
        <div class="category-name">{category.data.data.name}</div>
        <div class="category-actions" class:category-actions-space={category.length}>
            <button on:click|stopPropagation={() => {
                const clone = category.clone(deepClone)
                clone.data.selected = false
                clone.data.data.name += ' (copy)'
                category.parent.insert(
                    category.parent.indexOf(category) + 1,
                    clone,
                )
            }} title="Duplicate">
                <IconifyIcon icon={copyIcon}/>
            </button>
            {#if level < maxLevels - 1}
                <button on:click|stopPropagation={() => { addCategory(category) }} title="Add child">
                    <IconifyIcon icon={addChildIcon}/>
                </button>
            {/if}
        </div>
        {#if category.length}
            <button class="show-all-btn" class:opened={status} title="Show all"
                ondragover="return false"
                on:click|stopPropagation={() => {
                    status = !status
                }}
                on:dragenter={() => {
                    if ($isDragging) {
                        status = true
                    }
                }}>
                <IconifyIcon icon={downIcon}/>
            </button>
        {/if}
    </div>
    <div class="list-item__children" on:dragenter={() => {
            isHovering = true
        }}
        on:dragleave={() => {
            isHovering = false
        }}
        on:drop={() => {
            isHovering = false
        }}>
        <Categories
            on:select
            bind:categories={category}
            {maxLevels}
            {status}
            {searchText}
            level={level + 1}
            bind:firstDraggable
        />
    </div>
</div>
    {/if}

<style>
    .list-item__name {
        padding: 5px 8px;
        font-weight: 500;
        box-shadow: -2px 2px 3px rgba(10, 10, 10, 0.1), 0 0 0 1px rgba(10, 10, 10, 0.1);
        border-radius: 4px;
        font-size: 15px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        position: relative;
        margin-right: 1px;
        border-left: 4px solid #d2d2d2;
        cursor: pointer;
        background: white;
        user-select: none;
        transition: .15s;
    }

    .list-item__name.is-selected {
        border-color: #ff5d1c;
    }

    .list-item__name:hover {
        background: #eff1f5;
    }

    .list-item__name:before {
        content: "";
        width: 12px;
        height: 1px;
        background: lightgray;
        position: absolute;
        left: -17px;
        top: 50%;
    }

    .main-level > .list__items > .list-item > .list-item__name:before {
        display: none;
    }

    .list-item {
        display: block;
        padding: 8px 0 1px 12px;
        position: relative;
    }

    .list-item-wrapper + .list-item-wrapper .list-item {
        padding-bottom: 0;
    }

    .list-item__name.is-active {
        position: relative;
    }

    .list-item__name:after {
        content: "";
        height: 5px;
        width: calc(100% + 5px);
        background: #0080ff;
        position: absolute;
        top: -7px;
        right: 0;
        opacity: 0;
        transition: .2s;
        border-radius: 5px;
    }

    .list-item__name.is-active:after {
        opacity: 1;
    }

    .list-item__children {
        position: relative;
    }

    :global(.list__items > .list-item-wrapper:not(:last-child) > .list-item:before) {
        content: "";
        width: 1px;
        height: calc(100% - 25px);
        background: #dbdbdb;
        position: absolute;
        top: 25px;
        left: -1px;
    }

    .list-item__name.is-selected {
        color: #ff5d1c;
    }

    .dragging .list-item__name > * {
        pointer-events: none;
    }

    .category-actions {
        display: flex;
        opacity: 0;
        margin-right: -4px;
        transition: .25s;
    }

    .category-actions-space {
        margin-right: 24px;
    }

    .list-item__name:hover .category-actions {
        opacity: 1;
    }

    .category-actions button {
        border: 0;
        background: #eff1f5;
        color: #4a5463;
        border-radius: 4px;
        padding: 2px 1px;
        width: 26px;
        margin: -2px 0 -2px 3px;
        transition: .2s;
    }

    .show-all-btn {
        border: 0;
        position: absolute;
        background: none;
        color: #4a5463;
        margin-right: 4px;
        border-radius: 4px;
        padding: 0;
        right: 0;
        width: 25px;
        height: 100%;
        opacity: 0.65;
        transition: .2s;
    }

    .show-all-btn:hover {
        opacity: 0.8;
    }

    .show-all-btn.opened {
        transform: rotate(180deg);
    }

    .category-actions button:hover {
        background: #d0d5df;
    }

    .category-name:before {
        content: "";
        width: 1px;
        height: calc(100% - 7px);
        background: #dbdbdb;
        position: absolute;
        top: -9px;
        left: -17px;
    }

    .list-item.is-hover > .list-item__name {
        background: rgba(151, 199, 237, 0.3);
        transform: scale(1.02);
    }

    :global(.list.main-level>.list__items>.list-item>.list-item__name>.category-name:before) {
        left: -17px;
    }

    :global(.list.main-level>.list__items>.list-item:not(:last-child):before) {
        left: -17px;
    }

    :global(.list.main-level>.list__items>.list-item>.list-item__name:before) {
        display: none;
    }

    :global(.list__items > .list-item-wrapper:last-child > .list-item:before) {
        display: none;
    }

    :global(.list.main-level>.list__items>.list-item) {
        padding-top: 5px;
    }
</style>
